import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pb } from '@/lib/pocketbase';
import { Star } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { getOptimizedImageUrl } from '@/utils/imageOptimization';

interface HeroTutorCard {
  id: string;
  tutorId: string;
  displayOrder: number;
  enabled: boolean;
  expand?: {
    tutorId?: {
      id: string;
      name: string;
      profilePicture?: string;
      profilePictureUrl?: string;
    };
  };
}

interface HeroTutorCardsProps {
  tutors?: any[]; // Fallback tutors if no hero cards configured
}

const HeroTutorCards = ({ tutors = [] }: HeroTutorCardsProps) => {
  const navigate = useNavigate();

  // Fetch hero tutor cards from collection
  const { data: heroCards = [], isLoading } = useQuery({
    queryKey: ['hero-tutor-cards-display'],
    queryFn: async () => {
      try {
        const records = await pb.collection('hero_tutor_cards').getFullList<HeroTutorCard>({
          filter: 'enabled = true',
          sort: 'displayOrder',
          expand: 'tutorId',
          requestKey: `hero-cards-display-${Date.now()}`,
        });
        return records.slice(0, 4);
      } catch (error) {
        console.log('Hero tutor cards collection not found, using fallback');
        return [];
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Use hero cards if available, otherwise fall back to first 4 tutors
  const displayTutors = heroCards.length > 0
    ? heroCards.map(card => card.expand?.tutorId).filter(Boolean)
    : tutors.slice(0, 4);

  const getTutorName = (tutor: any) => {
    if (tutor.name && tutor.name.trim()) {
      return tutor.name;
    }
    if (tutor.expand?.user?.name) {
      return tutor.expand.user.name;
    }
    return 'Tutor';
  };

  const getProfilePictureUrl = (tutor: any) => {
    if (tutor.profilePicture && tutor.profilePicture.trim()) {
      // Use optimized 200x280 thumbnail for hero tutor cards
      return getOptimizedImageUrl(tutor, tutor.profilePicture, 200, 280);
    }
    if (tutor.profilePictureUrl && tutor.profilePictureUrl.trim()) {
      return tutor.profilePictureUrl;
    }
    return '';
  };

  const handleTutorClick = (tutorId: string) => {
    navigate(`/tutor/${tutorId}`);
  };

  // Loading skeleton
  if (isLoading || (heroCards.length === 0 && tutors.length === 0)) {
    return (
      <div className="w-full py-6 md:py-10">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-end -space-x-2 md:-space-x-4 lg:-space-x-5">
            {[0, 1, 2, 3].map((index) => {
              const isHigher = index % 2 === 0;
              return (
                <div
                  key={index}
                  className={`relative ${isHigher ? 'mb-6 md:mb-10' : 'mt-6 md:mt-10'}`}
                >
                  <Skeleton 
                    className="
                      w-[90px] h-[130px] 
                      md:w-[170px] md:h-[240px]
                      lg:w-[200px] lg:h-[280px]
                      rounded-lg
                    "
                    style={{ transform: 'skewY(-5deg)' }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (displayTutors.length === 0) {
    return null;
  }

  return (
    <div className="w-full py-6 md:py-10">
      <div className="container mx-auto px-4">
        <div className="flex justify-center items-end -space-x-2 md:-space-x-4 lg:-space-x-5">
          {displayTutors.map((tutor: any, index: number) => {
            // Cards 0, 2 (1st, 3rd) are higher; Cards 1, 3 (2nd, 4th) are lower
            const isHigher = index % 2 === 0;
            const profileUrl = getProfilePictureUrl(tutor);
            const name = getTutorName(tutor);
            
            return (
              <div
                key={tutor.id}
                onClick={() => handleTutorClick(tutor.id)}
                className={`
                  relative cursor-pointer group
                  transition-all duration-300 ease-out
                  hover:scale-105 hover:z-10
                  ${isHigher ? 'mb-6 md:mb-10' : 'mt-6 md:mt-10'}
                `}
              >
                {/* Parallelogram Card Container */}
                <div 
                  className="
                    relative overflow-hidden
                    w-[90px] h-[130px] 
                    md:w-[170px] md:h-[240px]
                    lg:w-[200px] lg:h-[280px]
                    shadow-lg
                    transition-shadow duration-300
                    group-hover:shadow-2xl
                  "
                  style={{
                    transform: 'skewY(-5deg)',
                    borderRadius: '8px',
                  }}
                >
                  {/* Inner content - counter-skew to keep image straight */}
                  <div 
                    className="absolute inset-0 overflow-hidden"
                    style={{
                      transform: 'skewY(5deg) scale(1.1)',
                      transformOrigin: 'center center',
                    }}
                  >
                    {/* Profile Image */}
                    {profileUrl ? (
                      <img
                        src={profileUrl}
                        alt={name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                        <span className="text-white text-2xl md:text-4xl font-bold">
                          {name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  </div>

                  {/* Top Tutor Badge - Centered, slightly lower */}
                  <div 
                    className="
                      pointer-events-none
                      absolute top-[65%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-20
                      animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]
                    "
                    style={{ transform: 'translate(-50%, -50%) skewY(5deg)' }}
                  >
                    <div className="
                      relative flex items-center gap-0.5 md:gap-1
                      bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500
                      text-white text-[7px] md:text-[11px] lg:text-xs font-bold
                      px-2 py-1 md:px-3 md:py-1.5
                      rounded-full shadow-lg
                      border border-amber-300/50
                      whitespace-nowrap
                    ">
                      <Star className="w-2.5 h-2.5 md:w-3.5 md:h-3.5 fill-white" />
                      <span>TOP</span>
                      {/* Shine effect */}
                      <div className="absolute inset-0 rounded-full overflow-hidden">
                        <div className="
                          absolute inset-0 -translate-x-full
                          bg-gradient-to-r from-transparent via-white/40 to-transparent
                          animate-shimmer
                        " />
                      </div>
                    </div>
                  </div>
                  
                  {/* Name Label - stays skewed with card */}
                  <div 
                    className="
                      absolute bottom-0 left-0 right-0 
                      p-2 md:p-3
                      text-center
                    "
                    style={{
                      transform: 'skewY(5deg)',
                    }}
                  >
                    <p className="text-white font-semibold text-[10px] md:text-sm lg:text-base truncate drop-shadow-lg">
                      {name.split(' ')[0]}
                    </p>
                  </div>
                </div>
                
                {/* Decorative glow on hover */}
                <div 
                  className="
                    absolute inset-0 -z-10 opacity-0 group-hover:opacity-100
                    transition-opacity duration-300
                    bg-gradient-to-r from-blue-400/30 to-purple-400/30
                    blur-xl
                  "
                  style={{
                    transform: 'skewY(-5deg)',
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HeroTutorCards;
